import { useEffect, useRef, useState } from "react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./embed-frame.i18n";

/**
 * Both design-token palettes, lifted out of the app's own stylesheet.
 *
 * Copying `getComputedStyle(document.documentElement)` would only ever give
 * the palette that is active right now — fine for rendering in place, useless
 * for a preview that has to show dark while the editor sits in light. Reading
 * the `:root` and `.dark` rules instead means the frame carries both and
 * switches on a class, exactly like the app does.
 *
 * The tenant's brand colour is not in the stylesheet — `app-shell` writes it
 * onto the element at runtime — so the inline style is appended to `:root`.
 */
type Palettes = { light: Record<string, string>; dark: Record<string, string> };

/** Last successful scan — see the cold-load note in `readPalettes`. */
let lastGoodPalettes: Palettes | null = null;

function parseDeclarations(cssText: string, into: Record<string, string>) {
  for (const decl of cssText.split(";")) {
    const idx = decl.indexOf(":");
    if (idx === -1) continue;
    const name = decl.slice(0, idx).trim();
    if (!name.startsWith("--")) continue;
    into[name] = decl.slice(idx + 1).trim();
  }
}

function readPalettes(): Palettes {
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};

  // Tailwind v4 wraps `@layer base { :root { … } }`, so the token rules sit
  // inside a grouping rule rather than at the top level of the sheet — walk
  // into anything that has nested rules or the scan comes back empty and every
  // widget silently falls back to its own hardcoded light defaults.
  const walk = (rules: CSSRuleList) => {
    for (const rule of Array.from(rules)) {
      const nested = (rule as CSSGroupingRule).cssRules;
      if (nested) walk(nested);
      const styleRule = rule as CSSStyleRule;
      if (!styleRule.selectorText || !styleRule.style?.getPropertyValue("--background")) continue;
      parseDeclarations(
        styleRule.style.cssText,
        styleRule.selectorText.includes(".dark") ? dark : light,
      );
    }
  };

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      walk(sheet.cssRules);
    } catch {
      // cross-origin sheet, nothing to read
    }
  }

  // Runtime overrides (the tenant brand colour) win over the stylesheet.
  parseDeclarations(document.documentElement.style.cssText, light);

  // On a cold load the stylesheet may not be attached yet, so the scan finds
  // nothing. Returning empty here is the bug that made every widget fall back
  // to its own light defaults — fine on a light page, a white slab on a dark
  // one. Hand back the last good read instead, and never cache an empty one.
  if (Object.keys(light).length === 0) return lastGoodPalettes ?? { light, dark };

  lastGoodPalettes = { light, dark: { ...light, ...dark } };
  return lastGoodPalettes;
}

/**
 * The app's token palettes as inline custom properties.
 *
 * Needed because Tailwind's `dark` variant only ever *adds* — a subtree cannot
 * opt back into light while an ancestor `html.dark` is setting the variables.
 * Writing the palette onto the element as inline style does override it, so the
 * preview canvas can show either theme regardless of what the app is in.
 */
export function themeVars(theme: "light" | "dark"): Record<string, string> {
  return readPalettes()[theme];
}

function palettesCss({ light, dark }: Palettes): string {
  const toBlock = (vars: Record<string, string>) =>
    Object.entries(vars)
      .map(([k, v]) => `${k}: ${v};`)
      .join(" ");
  return [`:root { ${toBlock(light)} }`, `.dark { ${toBlock(dark)} }`].join("\n  ");
}

/**
 * Origins a widget may pull scripts and styles from. Everything not listed is
 * blocked by the frame's CSP, so a widget cannot quietly call home to an
 * arbitrary host. Keep this short and boring — every entry is a third party
 * that sees the client's IP address when a flow page loads.
 */
export const EMBED_SCRIPT_ORIGINS = [
  "https://cdn.jsdelivr.net",
  "https://unpkg.com",
  "https://cdnjs.cloudflare.com",
];

/*
 * IDEA (not built): hand the widget a runtime context — currency, client name,
 * goal amount, language, and the tenant's date/number formats — as JSON on the
 * host element, e.g. `<div data-qp-context='{"currency":"NOK",…}'>`, for the
 * widget to read defensively. It makes a calculator label its own money and
 * format numbers the way the rest of the app does, and it is where QL-108's
 * regional settings would plug in. Needs a deliberate allowlist of fields —
 * this HTML is tenant-authored — and the flow renderer has to thread the real
 * client/goal data down to here first.
 */

type EmbedFrameProps = {
  html: string;
  /** Falls back to the widget's own measured height. */
  minHeight?: number;
  /** "auto" follows the app; the preview dialog forces one to compare. */
  theme?: "auto" | "light" | "dark";
};

/**
 * Renders embed HTML inside a sandboxed iframe — the ONLY renderer for embed
 * HTML (QL-776): it is never inserted into the app document. Third-party pages
 * (YouTube, forms) use the dedicated `iframe` component instead.
 *
 * This is not only an isolation choice — it is the only way the HTML runs at
 * all. React's `dangerouslySetInnerHTML` sets `innerHTML`, and the HTML spec
 * says script elements inserted that way never execute, so an embedded
 * calculator rendered that way is dead markup. A `srcdoc` document is a real
 * parse, so inline and external scripts run.
 *
 * `sandbox="allow-scripts allow-popups"` WITHOUT `allow-same-origin` gives the
 * document an opaque origin (popups inherit the sandbox): it cannot read the app's DOM, cookies or session, and cannot
 * call the app's API with the advisor's credentials. The CSP on top of that
 * limits where it may fetch code and images from.
 */
export function EmbedFrame({ html, minHeight = 120, theme = "auto" }: EmbedFrameProps) {
  const t = useT(T);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(minHeight);
  const [themeKey, setThemeKey] = useState(0);
  const [palettes, setPalettes] = useState<Palettes>(readPalettes);

  // Vite attaches the stylesheet after the first paint, so a frame rendered on
  // a cold load can start with no tokens at all. Keep looking until they land.
  useEffect(() => {
    if (Object.keys(palettes.light).length > 0) return;
    let frame = 0;
    let attempts = 0;
    const retry = () => {
      const next = readPalettes();
      if (Object.keys(next.light).length > 0) {
        setPalettes(next);
        return;
      }
      if (attempts++ < 120) frame = requestAnimationFrame(retry);
    };
    frame = requestAnimationFrame(retry);
    return () => cancelAnimationFrame(frame);
  }, [palettes]);

  // Poke the frame whenever its own box changes — the page reflowing, a column
  // narrowing, or a hidden tab panel becoming visible. Measuring is the frame's
  // job (cross-origin), so all we can do is ask.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const ask = () => el.contentWindow?.postMessage("qp-measure", "*");
    const observer = new ResizeObserver(ask);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The document reports its own height so the frame fits its content instead
  // of scrolling in a fixed box. Same-origin access is unavailable by design,
  // so measurement happens inside the frame and comes back by message.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      const next = Number((e.data as { qpEmbedHeight?: number })?.qpEmbedHeight);
      if (Number.isFinite(next) && next > 0) setHeight(Math.min(Math.ceil(next), 4000));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Re-render when the app flips between light and dark so the widget tracks
  // the theme toggle without a reload.
  useEffect(() => {
    if (theme !== "auto") return;
    const observer = new MutationObserver(() => setThemeKey((k) => k + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [theme]);

  const paletteCss = palettesCss(palettes);
  const isDark =
    theme === "auto" ? document.documentElement.classList.contains("dark") : theme === "dark";
  const scriptSrc = EMBED_SCRIPT_ORIGINS.join(" ");

  // `default-src 'none'` denies everything not named below. Note what is
  // absent: connect-src, so a widget cannot POST the client's data anywhere.
  const csp = [
    "default-src 'none'",
    `script-src 'unsafe-inline' ${scriptSrc}`,
    `style-src 'unsafe-inline' ${scriptSrc}`,
    "img-src https: data:",
    "font-src data:",
    // A nested https iframe is allowed, but it inherits this frame's sandbox
    // (no own origin), so players such as YouTube render blank here — pasted
    // third-party iframes belong in the Web page (URL) component.
    "frame-src https:",
    "media-src https:",
    "form-action 'none'",
  ].join("; ");

  const srcDoc = `<!doctype html>
<html${isDark ? ' class="dark"' : ""}><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  ${paletteCss}
  /* Without this the frame's used color-scheme is light while the page is
     dark, and Chrome paints the iframe an opaque WHITE canvas no matter how
     transparent html/body are. It also makes native controls (number inputs,
     scrollbars) render in the right scheme. */
  html { color-scheme: ${isDark ? "dark" : "light"}; }
  html,body { margin:0; padding:0; background:transparent; color:hsl(var(--foreground));
    font-family:ui-sans-serif,system-ui,sans-serif; font-size:16px; line-height:1.5; }
  /* The page background belongs to the platform. A widget that paints its own
     panel behind everything looks pasted-on in light mode and stays white in
     dark, so the root is forced transparent whatever the HTML asks for. Inner
     elements keep their fills — this only overrides the outermost wrapper. */
  body > .qp-embed-host > * { background: transparent !important; }
</style></head>
<body>
<div class="qp-embed-host">${html}</div>
<script>
  (function () {
    // Measure the content wrapper, NOT document.documentElement: inside a frame
    // its scrollHeight is never less than the frame's own height, so feeding
    // that back as the new height ratchets the frame taller on every remeasure.
    var host = document.querySelector(".qp-embed-host");
    var report = function () {
      var h = host ? host.getBoundingClientRect().height : document.body.scrollHeight;
      parent.postMessage({ qpEmbedHeight: Math.ceil(h) }, "*");
    };
    window.addEventListener("load", report);
    // The frame's own width changes when the page reflows — a widget whose
    // inputs wrap onto a second row gets taller, and without this it keeps the
    // old height and scrolls inside itself.
    window.addEventListener("resize", report);
    if (host) new ResizeObserver(report).observe(host);
    // The host asks for a fresh measurement when it resizes us or reveals us
    // (a tab panel that was display:none measures as zero).
    window.addEventListener("message", function (e) {
      if (e.data === "qp-measure") report();
    });
    // External libraries finish after load; keep measuring briefly.
    [100, 500, 1500].forEach(function (t) { setTimeout(report, t); });
    report();
  })();
</script>
</body></html>`;

  return (
    <iframe
      key={`${themeKey}-${theme}`}
      ref={frameRef}
      title={t.title}
      srcDoc={srcDoc}
      // Popups need a user gesture and INHERIT this sandbox (no
      // `allow-popups-to-escape-sandbox`), so an opened tab can never run as
      // the app origin. Never add `allow-same-origin` here (QL-776).
      sandbox="allow-scripts allow-popups"
      className="w-full border-0 bg-transparent"
      style={{ height }}
    />
  );
}
